/**
 * FamiliarisationModal - Tailwind-compatible vessel familiarisation workflow
 * WBC3 Section 12.4 compliant with conditional logic for vessel types and crew ranks
 */
import React, { useState, useEffect, useMemo } from 'react';
import { X, Shield } from 'lucide-react';
import { useVessel } from '../../../../contexts/VesselContext';
// import { useVesselCollections } from '../../../../hooks/useVesselCollections'; // Phase 2.6: Disabled for IndexedDB migration
import { COLLECTIONS, createBaseRecord } from '../../../../types/core';
import { generateFamiliarisationWithCustomItems } from '../../../../utils/familiarisationHelpers';
import { useStorageState } from '../../../../hooks/useStorageState';
import { Modal, ModalBody, ModalActions } from '../../common/Modal';
import FamiliarisationSectionContent from './FamiliarisationSectionContent';
import FamiliarisationSignOff from './FamiliarisationSignOff';
import FamiliarisationNavigation from './FamiliarisationNavigation';
import FamiliarisationNADialog from './FamiliarisationNADialog';
import FamiliarisationFooter from './FamiliarisationFooter';

const FamiliarisationModal = ({ isOpen, onClose, crew, onComplete, viewMode = false }) => {
  // const { vesselId, collections } = useVesselCollections(); // Phase 2.6: Disabled for IndexedDB migration
  // Phase 2.6: collections disabled — stub to prevent crash
  const collections = {
    list: async () => [],
    upsert: async () => null,
    get: async () => null,
    remove: async () => null,
  };
  const { getCurrentVessel, currentVesselId } = useVessel();
  const currentVessel = getCurrentVessel();
  const vesselId = currentVesselId; // Use currentVesselId from VesselContext
  // Note: Draft state auto-save temporarily disabled - completed records still save via useStorageState
  const { value: customItems = [] } = useStorageState('customFamiliarisationItems', []);
  const [completedTopics, setCompletedTopics] = useState({});
  const [completedChecklists, setCompletedChecklists] = useState({});
  const [topicNotes, setTopicNotes] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [familiarisationComplete, setFamiliarisationComplete] = useState(false);
  const [crewSignature, setCrewSignature] = useState('');
  const [assessorSignature, setAssessorSignature] = useState('');
  const [completionDate, setCompletionDate] = useState('');
  const [crewPosition, setCrewPosition] = useState('');
  const [assessorPosition, setAssessorPosition] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showNADialog, setShowNADialog] = useState({ show: false, item: null, topicId: null });
  const [selectedNAReason, setSelectedNAReason] = useState('');
  const [paperMode, setPaperMode] = useState(false);
  // Paper mode specific fields
  const [paperCompletionDate, setPaperCompletionDate] = useState('');
  const [paperChecklistFile, setPaperChecklistFile] = useState(null);
  const [paperNotes, setPaperNotes] = useState('');

  const vesselChecklists = useMemo(() => {
    if (!currentVessel || !crew) return {};
    try {
      const familiarisationData = generateFamiliarisationWithCustomItems(currentVessel, crew, customItems);
      const checklists = {};
      familiarisationData.sections.forEach(section => {
        checklists[section.section] = {
          ...section,
          items: section.items || []
        };
      });
      
      return checklists;
    } catch (error) {
      return {};
    }
  }, [currentVessel, crew, customItems]);
  const sections = useMemo(() => {
    if (!currentVessel || !crew) return [];
    try {
      const familiarisationData = generateFamiliarisationWithCustomItems(currentVessel, crew, customItems);
      return familiarisationData.sections || [];
    } catch (error) {
      return [];
    }
  }, [currentVessel, crew, customItems]);

  const saveProgressToStorage = async () => {
    if (!crew?.id || !vesselId) return;
    
    try {
      const progressData = createBaseRecord({
        id: `familiarisation_progress_${crew.id}`,
        crewId: crew.id, 
        vesselId: vesselId, 
        completedTopics, 
        completedChecklists,
        topicNotes, 
        currentStep, 
        signOffData: { 
          familiarisationComplete, 
          crewSignature,
          assessorSignature, 
          completionDate, 
          crewPosition, 
          assessorPosition 
        },
        status: 'in_progress',
        lastSaved: new Date().toISOString(),
        origin: 'manual',
        // Paper mode progress
        paperModeProgress: paperMode ? {
          paperCompletionDate,
          paperChecklistFile,
          paperNotes
        } : null
      });

      // Phase 2.6: Disabled - collections removed
      // await collections.upsert('familiarisation_progress', progressData);
    } catch (error) {

    }
  };

  const loadProgressFromStorage = async () => {
    if (!crew?.id || !vesselId) return;
    
    try {
      const saved = await collections.get('familiarisation_progress', `familiarisation_progress_${crew.id}`);
      if (saved) {
        setCompletedTopics(saved.completedTopics || {});
        setCompletedChecklists(saved.completedChecklists || {});
        setTopicNotes(saved.topicNotes || {});
        setCurrentStep(saved.currentStep || 0);
        if (saved.signOffData) {
          setFamiliarisationComplete(saved.signOffData.familiarisationComplete || false);
          setCrewSignature(saved.signOffData.crewSignature || '');
          setAssessorSignature(saved.signOffData.assessorSignature || '');
          setCompletionDate(saved.signOffData.completionDate || '');
          setCrewPosition(saved.signOffData.crewPosition || '');
          setAssessorPosition(saved.signOffData.assessorPosition || '');
        }
        // Restore paper mode progress
        if (saved.paperModeProgress) {
          setPaperCompletionDate(saved.paperModeProgress.paperCompletionDate || '');
          setPaperChecklistFile(saved.paperModeProgress.paperChecklistFile || null);
          setPaperNotes(saved.paperModeProgress.paperNotes || '');
        }
      }
    } catch (error) {

    }
  };

  useEffect(() => {
    if (isOpen && !viewMode && crew?.id && vesselId) {
      (async () => {
        await saveProgressToStorage();
        // Reset unsaved changes flag after saving
        setTimeout(() => setHasUnsavedChanges(false), 100);
      })();
    }
  }, [completedTopics, completedChecklists, topicNotes, familiarisationComplete, crewSignature, assessorSignature, completionDate, crewPosition, assessorPosition, isOpen, viewMode, crew?.id, vesselId]);

  useEffect(() => {
    if (isOpen && crew?.id && !viewMode && vesselId) {
      (async () => {
        await loadProgressFromStorage();
        
        // Load paper mode setting from collections
        try {
          const paperModeId = `familiarisationPaperMode:${vesselId}`;
          // const paperModeData = await collections. // Phase 2.6: Disabledget('meta', paperModeId);
          if (paperModeData?.enabled !== undefined) {
            setPaperMode(paperModeData.enabled);
          }
        } catch (error) {

        }
      })();
    }
  }, [isOpen, crew?.id, viewMode, vesselId, collections]);

  // Jump to sign-off if paper mode is enabled
  useEffect(() => {
    if (paperMode && isOpen && sections.length > 0) {
      setCurrentStep(sections.length);
    }
  }, [paperMode, isOpen, sections.length]);

  useEffect(() => {
    setHasUnsavedChanges(true);
  }, [completedTopics, completedChecklists, topicNotes, familiarisationComplete, crewSignature, assessorSignature, completionDate, crewPosition, assessorPosition]);

  const handleChecklistItemStatus = (topicId, itemId, status, reason = null) => {
    setCompletedChecklists(prev => {
      const newState = { ...prev };
      if (!newState[topicId]) newState[topicId] = {};
      if (status === null) {
        delete newState[topicId][itemId];
      } else {
        newState[topicId][itemId] = {
          status, reason,
          timestamp: new Date().toISOString(),
          completedBy: crew?.full_name || 'Unknown'
        };
      }
      return newState;
    });
  };

  const handleTopicComplete = (topicId, isCompleted) => {
    setCompletedTopics(prev => ({
      ...prev,
      [topicId]: isCompleted
    }));
  };

  const isTopicComplete = (topicId) => {
    const checklistData = vesselChecklists[topicId];
    if (!checklistData) return false;
    const checklist = checklistData.items || [];
    const completedItems = completedChecklists[topicId] || {};
    const criticalItems = checklist.filter(item => item.priority === 'critical');
    const criticalCompleted = criticalItems.every(item => {
      const itemStatus = completedItems[item.id];
      if (!itemStatus) return false;
      if (itemStatus.status === 'completed') return true;
      if (itemStatus.status === 'na') return itemStatus.reason && itemStatus.reason.trim() !== '';
      return false;
    });
    return criticalCompleted && completedTopics[topicId];
  };

  const handleTopicNotes = (topicId, notes) => {
    setTopicNotes(prev => ({
      ...prev,
      [topicId]: notes
    }));
  };

  const handleCompleteFamiliarisation = async () => {
    // Paper mode validation - require date and file upload
    if (paperMode && (!paperCompletionDate || !paperChecklistFile)) {
      alert('Please provide the paper completion date and upload the scanned checklist.');
      return;
    }

    const allTopicsCompleted = sections.every(section =>
      section.mandatory ? isTopicComplete(section.section) : true
    );

    if (!allTopicsCompleted || !familiarisationComplete || !crewSignature || !assessorSignature) {
      alert('Please complete all mandatory topics and sign-off before finishing.');
      return;
    }

    setIsSubmitting(true);

    try {
      const familiarisationRecord = {
        crewId: crew.id,
        crewName: crew.name || crew.full_name,
        vesselId: currentVessel?.id,
        vesselName: currentVessel?.name,
        vesselType: currentVessel?.type,
        category: currentVessel?.category,
        completedTopics,
        completedChecklists,
        vesselChecklists,
        topicNotes,
        signOffData: {
          familiarisationComplete,
          crewSignature,
          assessorSignature,
          completionDate,
          crewPosition,
          assessorPosition
        },
        completedAt: new Date().toISOString(),
        complianceLevel: 'WBC3_A8_S7',
        checklistVersion: '1.0',
        // Paper mode data
        paperMode: paperMode,
        paperModeData: paperMode ? {
          paperCompletionDate,
          paperChecklistFile: paperChecklistFile ? {
            fileName: paperChecklistFile.fileName,
            fileType: paperChecklistFile.fileType,
            size: paperChecklistFile.size,
            fileData: paperChecklistFile.fileData,
            uploadedAt: paperChecklistFile.uploadedAt
          } : null,
          paperNotes: paperNotes || null
        } : null
      };

      onComplete(familiarisationRecord);
      
      // Clean up progress data from collections
      try {
        // Phase 2.6: Disabled - collections removed
        // await collections.remove('familiarisation_progress', `familiarisation_progress_${crew.id}`);
      } catch (error) {

      }
      onClose();
      
    } catch (error) {
      alert('Error completing familiarisation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const goToNext = () => {
    if (!paperMode && currentStep < sections.length) {
      setCurrentStep(currentStep + 1);
    }
  };
  const goToPrevious = () => {
    if (!paperMode && currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  const goToTopic = (index) => {
    if (!paperMode) {
      setCurrentStep(index);
    }
  };
  const handleGoToSignOff = () => setCurrentStep(sections.length);
  const handleClearProgress = () => {
    setCompletedTopics({});
    setCompletedChecklists({});
    setTopicNotes({});
    setCurrentStep(0);
    setFamiliarisationComplete(false);
    setCrewSignature('');
    setAssessorSignature('');
    setCompletionDate('');
    setCrewPosition('');
    setAssessorPosition('');
  };

  const handleClose = () => {
    if (!viewMode && hasUnsavedChanges) {
      const shouldSave = window.confirm('You have unsaved progress. Would you like to save before closing?');
      if (shouldSave) {
        saveProgressToStorage();
      }
    }
    onClose();
  };

  const handleNAClick = (topicId, item) => {
    setShowNADialog({ show: true, item, topicId });
    setSelectedNAReason('');
  };

  const handleNAConfirm = (reasonWithCustom) => {
    if (showNADialog.item) {
      // If a custom reason is provided (format: "other: custom text"), use it
      // Otherwise use the selected reason
      const finalReason = reasonWithCustom || selectedNAReason;
      
      if (finalReason) {
        handleChecklistItemStatus(
          showNADialog.topicId,
          showNADialog.item.id,
          'na',
          finalReason
        );
      }
    }
    setShowNADialog({ show: false, item: null, topicId: null });
    setSelectedNAReason('');
  };

  const completedCount = sections.filter(section => isTopicComplete(section.section)).length;
  const allTopicsCompleted = paperMode || sections.every(section => 
    section.mandatory ? isTopicComplete(section.section) : true
  );

  if (!crew) return null;

  const modalTitle = `Vessel Familiarisation - ${crew.name || crew.full_name}`;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={modalTitle} size="5xl">
      <ModalBody>
        {/* Paper Mode Notice */}
        {paperMode && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-amber-900">Paper Mode Active</p>
                <p className="text-xs text-amber-700 mt-1">
                  Please complete your paper familiarisation checklist before signing off below.
                </p>
              </div>
              <button
                onClick={async () => {
                  setPaperMode(false);
                  try {
                    const paperModeId = `familiarisationPaperMode:${vesselId}`;
                    // Phase 2.6: Disabled - collections removed
                    // await collections.upsert('meta', {
                    //   id: paperModeId,
                    //   vesselId: vesselId,
                    //   enabled: false,
                    //   origin: 'manual'
                    // });
                  } catch (error) {

                  }
                  setCurrentStep(0);
                }}
                className="text-sm text-amber-700 hover:text-amber-900 underline"
              >
                Exit Paper Mode
              </button>
            </div>
          </div>
        )}

        {/* Status Bar - Hidden in Paper Mode */}
        {!paperMode && (
          <div className="bg-cyan-50 rounded-lg p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-cyan-600" />
              <div>
                <p className="text-sm font-medium text-cyan-900">{currentVessel?.name}</p>
                <p className="text-xs text-cyan-700">{viewMode ? 'Read-Only Mode' : 'Interactive Training'}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-cyan-900">{completedCount} of {sections.length} sections complete</p>
              <p className="text-xs text-cyan-700">Progress: {Math.round((completedCount / sections.length) * 100)}%</p>
            </div>
          </div>
        )}
        
        {/* Progress Bar - Hidden in Paper Mode */}
        {!paperMode && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Progress</span>
              <span className="text-sm text-gray-600">
                {Math.round((completedCount / sections.length) * 100)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-cyan-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(completedCount / sections.length) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Navigation Pills - Hidden in Paper Mode */}
        {!paperMode && (
          <FamiliarisationNavigation
            sections={sections}
            currentStep={currentStep}
            familiarisationComplete={familiarisationComplete}
            isTopicComplete={isTopicComplete}
            onGoToTopic={goToTopic}
            onGoToSignOff={handleGoToSignOff}
          />
        )}

        {/* Content */}
        <div className="mt-6 mb-4">
            {(currentStep < sections.length && !paperMode) ? (
              <FamiliarisationSectionContent
                section={sections[currentStep]}
                checklistData={vesselChecklists[sections[currentStep].section]}
                completedItems={completedChecklists[sections[currentStep].section] || {}}
                isTopicCompleted={completedTopics[sections[currentStep].section]}
                notes={topicNotes[sections[currentStep].section] || ''}
                onItemStatusChange={handleChecklistItemStatus}
                onTopicComplete={handleTopicComplete}
                onNotesChange={handleTopicNotes}
                onNAClick={handleNAClick}
                viewMode={viewMode}
              />
            ) : (paperMode || currentStep >= sections.length) ? (
              <FamiliarisationSignOff
                familiarisationComplete={familiarisationComplete}
                crewSignature={crewSignature}
                assessorSignature={assessorSignature}
                completionDate={completionDate}
                crewPosition={crewPosition}
                assessorPosition={assessorPosition}
                allTopicsCompleted={allTopicsCompleted}
                onFamiliarisationComplete={setFamiliarisationComplete}
                onCrewSignature={setCrewSignature}
                onAssessorSignature={setAssessorSignature}
                onCompletionDate={setCompletionDate}
                onCrewPosition={setCrewPosition}
                onAssessorPosition={setAssessorPosition}
                viewMode={viewMode}
                paperMode={paperMode}
                // Paper mode props
                paperCompletionDate={paperCompletionDate}
                onPaperCompletionDate={setPaperCompletionDate}
                paperChecklistFile={paperChecklistFile}
                onPaperChecklistFile={setPaperChecklistFile}
                paperNotes={paperNotes}
                onPaperNotes={setPaperNotes}
              />
            ) : null}
        </div>
        
        {/* Footer - Moved inside ModalBody to ensure visibility */}
        <div className="mt-6">
          <FamiliarisationFooter
            currentStep={currentStep}
            sectionsLength={sections.length}
            viewMode={viewMode}
            allTopicsCompleted={allTopicsCompleted}
            familiarisationComplete={familiarisationComplete}
            crewSignature={crewSignature}
            assessorSignature={assessorSignature}
            isSubmitting={isSubmitting}
            onPrevious={goToPrevious}
            onNext={goToNext}
            onClearProgress={handleClearProgress}
            onComplete={handleCompleteFamiliarisation}
            crewId={crew.id}
          />
        </div>
        
        {/* Extra padding to ensure we can scroll to see buttons */}
        <div className="h-20"></div>
      </ModalBody>

      {/* N/A Reason Dialog */}
      <FamiliarisationNADialog
        show={showNADialog.show}
        item={showNADialog.item}
        selectedNAReason={selectedNAReason}
        onReasonChange={setSelectedNAReason}
        onConfirm={handleNAConfirm}
        onCancel={() => setShowNADialog({ show: false, item: null, topicId: null })}
      />
    </Modal>
  );
};

export default FamiliarisationModal;